import { AIModelsCard } from "@/components/AIModelsCard";
import { useEffect, useRef, useState } from "react";
import { ImageUp } from "lucide-react";
import { toast } from "sonner";
import { PageHeader } from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { API, api, errMsg } from "@/lib/api";

export default function Settings() {
  const [s, setS] = useState(null);
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);
  useEffect(() => {
    api.get("/settings").then((r) => setS(r.data));
  }, []);

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { brand_name, footer_text, website, series_note, reply_to_email, notify_email } = s;
      const r = await api.put("/settings", { brand_name, footer_text, website, series_note, reply_to_email, notify_email });
      setS(r.data);
      toast.success("Brand settings saved. Rebuild a title's files to apply them.");
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  const upload = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const fd = new FormData();
    fd.append("file", f);
    try {
      const r = await api.post("/settings/logo", fd);
      setS(r.data);
      toast.success("Logo uploaded");
    } catch (err) {
      toast.error(errMsg(err));
    }
  };

  if (!s) return null;
  const F = (k, label, ph) => (
    <div>
      <Label>{label}</Label>
      <Input value={s[k] || ""} placeholder={ph} onChange={(e) => setS({ ...s, [k]: e.target.value })} className="mt-2 bg-white" data-testid={`settings-${k.replace(/_/g, "-")}-input`} />
    </div>
  );

  return (
    <div>
      <PageHeader eyebrow="Applied to every document" title="Brand & Logo" />
      <div className="px-6 md:px-12 py-10 grid lg:grid-cols-12 gap-10">
        <form onSubmit={save} className="lg:col-span-6 bg-[#FFFDF9] border border-[#2A2325]/10 p-8 space-y-5">
          {F("brand_name", "Publisher name", "Soft Spine Press")}
          {F("footer_text", "Page footer text (after ©year)", "Soft Spine Press · AI-assisted research, human-crafted judgement")}
          {F("series_note", "Back cover note", "One of an occasional series…")}
          {F("website", "Website on back cover", "softspinepress.com")}
          {F("reply_to_email", "Reply-to email for customer emails", "hello@softspinepress.com")}
          {F("notify_email", "Your email for update reminders", "you@softspinepress.com")}
          <Button type="submit" disabled={busy} className="rounded-none" data-testid="settings-save-btn">Save brand settings</Button>
        </form>
        <div className="lg:col-span-6">
          <div className="bg-[#38171E] aspect-[3/4] max-w-sm relative flex flex-col items-center justify-end pb-14" data-testid="logo-backcover-preview">
            <div className="absolute inset-4 border border-[#D4AF37]/70" />
            <div className="absolute top-14 left-10 right-10 text-center">
              <div className="text-[#D4AF37]">❦</div>
              <div className="font-display text-[#F3E5AB] tracking-[0.15em] uppercase text-xl mt-4">Back cover</div>
              <p className="font-body-serif text-[#EDE3C8]/80 text-sm mt-4 italic">Your logo appears here on every PDF and HTML.</p>
            </div>
            {s.logo_path ? (
              <div className="bg-[#FBF7EF] border border-[#D4AF37] rounded-md px-4 py-3">
                <img src={`${API}/public/logo?v=${s.logo_updated_at}`} alt="Logo" className="block max-w-[170px] max-h-[110px] object-contain" data-testid="logo-preview-img" />
              </div>
            ) : (
              <>
                <div className="text-[#D4AF37] text-2xl">❦</div>
                <div className="font-display text-[#F3E5AB] tracking-[0.3em] text-xs mt-4">{(s.brand_name || "").toUpperCase()}</div>
              </>
            )}
          </div>
          <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/webp,image/svg+xml" className="hidden" onChange={upload} data-testid="logo-file-input" />
          <Button variant="outline" className="rounded-none mt-5 gap-2 bg-transparent" onClick={() => fileRef.current.click()} data-testid="logo-upload-btn"><ImageUp size={15} /> {s.logo_path ? "Replace logo" : "Upload logo"}</Button>
          <p className="text-xs text-[#5C5255] mt-2">PNG with transparent background works best. Under 5 MB.</p>
        </div>
      </div>
      <div className="px-6 md:px-12 pb-12"><AIModelsCard /></div>
    </div>
  );
}
